import { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { fetchDataFromAPI } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ/api/dataGridApiService';
import type {
  FilterTrayCategory,
  FilterTrayOption,
  FilterTraySelection,
} from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ';

type CreatedAtFilterModel = {
  items: Array<{
    id?: number;
    columnField: string;
    operatorValue: string;
    value?: any;
  }>;
  linkOperator?: 'and' | 'or';
};

const RUN_LOGS_ENDPOINT = 'api/v2/run-v2';
const FILTER_TRAY_PAGE_SIZE = 500;
const RUN_LOGS_OPTION_FILTER_KEYS = [
  '_id',
  'name',
  'run_type',
  'run_by_name',
  'status',
] as const;
const RUN_LOGS_CATEGORY_KEYS = [...RUN_LOGS_OPTION_FILTER_KEYS, 'created_at'] as const;
const CREATED_AT_CATEGORY_ID = 'created_at';

const RUN_LOGS_CATEGORY_LABELS: Record<string, string> = {
  _id: 'Run Id',
  name: 'Name',
  run_type: 'Run Type',
  run_by_name: 'Run By Name',
  status: 'Status',
  created_at: 'Created At',
};

function toStr(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function deriveValueMap(rows: any[]): Record<string, string[]> {
  const valueSetByKey: Record<string, Set<string>> = {};

  for (const row of rows) {
    for (const key of RUN_LOGS_OPTION_FILTER_KEYS) {
      if (!valueSetByKey[key]) valueSetByKey[key] = new Set<string>();
      const value = toStr(row?.[key]).trim();
      if (value !== '') valueSetByKey[key].add(value);
    }
  }

  const valueMap: Record<string, string[]> = {};
  for (const key of RUN_LOGS_OPTION_FILTER_KEYS) {
    valueMap[key] = Array.from(valueSetByKey[key] ?? []).sort((a, b) =>
      a.localeCompare(b),
    );
  }

  return valueMap;
}

export interface UseRunLogsFilterTrayResult {
  categories: FilterTrayCategory[];
  optionsForActiveCategory: FilterTrayOption[];
  selectedByCategory: FilterTraySelection;
  onSelectionChange: (categoryId: string, selectedValues: string[]) => void;
  onClearAllFilters: () => void;
  onClearActiveCategory: () => void;
  onClearAllInPanel: () => void;
  quickFilters: Record<string, string[]>;
  createdAtStartDate: string | null;
  createdAtEndDate: string | null;
  onCreatedAtStartDateChange: (value: string | null) => void;
  onCreatedAtEndDateChange: (value: string | null) => void;
  onClearCreatedAtRange: () => void;
  createdAtFilterModel: CreatedAtFilterModel;
  totalActiveCount: number;
  activeCategoryId: string | null;
  setActiveCategoryId: (id: string | null) => void;
  isLoading: boolean;
  error: Error | null;
}

export function useRunLogsFilterTray(dealerId: string): UseRunLogsFilterTrayResult {
  const [selectedByCategory, setSelectedByCategory] = useState<FilterTraySelection>(
    {},
  );
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [createdAtStartDate, setCreatedAtStartDate] = useState<string | null>(null);
  const [createdAtEndDate, setCreatedAtEndDate] = useState<string | null>(null);

  const quickFilters = useMemo(() => {
    const output: Record<string, string[]> = {};
    for (const [key, values] of Object.entries(selectedByCategory)) {
      if (
        key !== CREATED_AT_CATEGORY_ID &&
        Array.isArray(values) &&
        values.length > 0
      ) {
        output[key] = values;
      }
    }
    return output;
  }, [selectedByCategory]);

  const params = useMemo(
    () => ({
      dealer: dealerId,
      limit: FILTER_TRAY_PAGE_SIZE,
      ...Object.fromEntries(
        Object.entries(quickFilters).map(([key, values]) => [
          key,
          values.join(','),
        ]),
      ),
    }),
    [dealerId, quickFilters],
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['run-logs-filter-tray', dealerId, params],
    queryFn: () =>
      fetchDataFromAPI(RUN_LOGS_ENDPOINT, params, 1, FILTER_TRAY_PAGE_SIZE),
    enabled: Boolean(dealerId),
  });

  const valueMap = useMemo(() => {
    const rows = data?.body?.data ?? [];
    return deriveValueMap(rows);
  }, [data]);

  const categories: FilterTrayCategory[] = useMemo(
    () =>
      RUN_LOGS_CATEGORY_KEYS.map(key => ({
        id: key,
        label:
          RUN_LOGS_CATEGORY_LABELS[key] ??
          key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()),
        selectedCount:
          key === CREATED_AT_CATEGORY_ID
            ? createdAtStartDate && createdAtEndDate
              ? 1
              : 0
            : (selectedByCategory[key] ?? []).length,
      })),
    [selectedByCategory, createdAtStartDate, createdAtEndDate],
  );

  const optionsForActiveCategory: FilterTrayOption[] = useMemo(() => {
    if (!activeCategoryId) return [];
    if (activeCategoryId === CREATED_AT_CATEGORY_ID) return [];
    const optionsFromData = valueMap[activeCategoryId] ?? [];
    const selectedOptions = selectedByCategory[activeCategoryId] ?? [];
    const combined = Array.from(
      new Set([...optionsFromData, ...selectedOptions]),
    ).sort((a, b) => a.localeCompare(b));
    return combined.map(value => ({ value, label: value }));
  }, [activeCategoryId, valueMap, selectedByCategory]);

  const onSelectionChange = useCallback(
    (categoryId: string, selectedValues: string[]) => {
      setSelectedByCategory(previous => ({
        ...previous,
        [categoryId]: selectedValues,
      }));
    },
    [],
  );

  const onClearAllFilters = useCallback(() => {
    setSelectedByCategory({});
    setCreatedAtStartDate(null);
    setCreatedAtEndDate(null);
  }, []);

  const onClearActiveCategory = useCallback(() => {
    if (!activeCategoryId) return;
    if (activeCategoryId === CREATED_AT_CATEGORY_ID) {
      setCreatedAtStartDate(null);
      setCreatedAtEndDate(null);
      return;
    }
    setSelectedByCategory(previous => ({
      ...previous,
      [activeCategoryId]: [],
    }));
  }, [activeCategoryId]);

  const onClearAllInPanel = useCallback(() => {
    setSelectedByCategory({});
    setCreatedAtStartDate(null);
    setCreatedAtEndDate(null);
  }, []);

  const onCreatedAtStartDateChange = useCallback((value: string | null) => {
    setCreatedAtStartDate(value);
  }, []);

  const onCreatedAtEndDateChange = useCallback((value: string | null) => {
    setCreatedAtEndDate(value);
  }, []);

  const onClearCreatedAtRange = useCallback(() => {
    setCreatedAtStartDate(null);
    setCreatedAtEndDate(null);
  }, []);

  const createdAtFilterModel = useMemo<CreatedAtFilterModel>(() => {
    if (!createdAtStartDate || !createdAtEndDate) {
      return { items: [] };
    }

    const startBoundary = DateTime.fromISO(createdAtStartDate)
      .minus({ milliseconds: 1 })
      .toUTC()
      .toISO();
    const endBoundary = DateTime.fromISO(createdAtEndDate)
      .plus({ days: 1 })
      .toUTC()
      .toISO();

    if (!startBoundary || !endBoundary) {
      return { items: [] };
    }

    return {
      items: [
        {
          id: 1,
          columnField: CREATED_AT_CATEGORY_ID,
          operatorValue: 'after',
          value: startBoundary,
        },
        {
          id: 2,
          columnField: CREATED_AT_CATEGORY_ID,
          operatorValue: 'before',
          value: endBoundary,
        },
      ],
      linkOperator: 'and',
    };
  }, [createdAtStartDate, createdAtEndDate]);

  const totalActiveCount = useMemo(
    () => {
      const selectedCount = Object.values(selectedByCategory).reduce(
        (total, selectedValues) => total + (selectedValues?.length ?? 0),
        0,
      );
      const createdAtCount = createdAtStartDate && createdAtEndDate ? 1 : 0;
      return selectedCount + createdAtCount;
    },
    [selectedByCategory, createdAtStartDate, createdAtEndDate],
  );

  return {
    categories,
    optionsForActiveCategory,
    selectedByCategory,
    onSelectionChange,
    onClearAllFilters,
    onClearActiveCategory,
    onClearAllInPanel,
    quickFilters,
    createdAtStartDate,
    createdAtEndDate,
    onCreatedAtStartDateChange,
    onCreatedAtEndDateChange,
    onClearCreatedAtRange,
    createdAtFilterModel,
    totalActiveCount,
    activeCategoryId,
    setActiveCategoryId,
    isLoading: !!isLoading || !!isFetching,
    error: error as Error | null,
  };
}
